import React from '../../vendor/react';
import { ArrowRight, ArrowRightLeft, Banknote, CheckCircle2, ChevronDown, CreditCard, WalletCards } from '../../vendor/lucide-react';
import { useEffect, useState } from '../../vendor/react';
import type { ReactNode } from '../../vendor/react';
import type { PosPaymentPreference } from '../../hooks/useSystemPreferences';
import { formatMoney } from '../../lib/money';
import type { CartItem, Customer, PaymentMethod, SaleMode } from '../../types/domain';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

type PaymentInput = {
  payments: { method: PaymentMethod; amountCents: number; reference?: string }[];
  changeCents: number;
  dueDate?: string;
  promisedDate?: string;
  notes: string;
};

type PaymentModalProps = {
  open: boolean;
  mode: SaleMode;
  totalCents: number;
  items: CartItem[];
  customer?: Customer;
  defaultPayment: PosPaymentPreference;
  onClose: () => void;
  onComplete: (input: PaymentInput) => void;
};

const toInputDate = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};

const toCents = (value: string) => Math.max(0, Math.round(Number(value || 0) * 100));

export function PaymentModal({ open, mode, totalCents, items, customer, defaultPayment, onClose, onComplete }: PaymentModalProps) {
  const [cash, setCash] = useState('');
  const [received, setReceived] = useState('');
  const [transfer, setTransfer] = useState('');
  const [card, setCard] = useState('');
  const [credit, setCredit] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [dueDate, setDueDate] = useState(toInputDate(30));
  const [promisedDate, setPromisedDate] = useState(toInputDate(7));
  const [notes, setNotes] = useState('');
  const quickAmountCents = mode === 'sale'
    ? totalCents
    : Math.max(1, Math.round(totalCents * Math.max(20, ...items.map((item) => item.product.depositPercent ?? 0)) / 100));
  const amount = (quickAmountCents / 100).toFixed(2);

  useEffect(() => {
    if (!open) return;
    setCash(defaultPayment === 'cash' ? amount : '');
    setReceived(defaultPayment === 'cash' ? amount : '');
    setTransfer(defaultPayment === 'transfer' ? amount : '');
    setCard(defaultPayment === 'card' ? amount : '');
    setCredit('');
    setAdvancedOpen(mode !== 'sale');
    setDueDate(toInputDate(30));
    setPromisedDate(toInputDate(7));
    setNotes('');
  }, [open]);

  const cashCents = toCents(cash);
  const receivedCents = toCents(received);
  const transferCents = toCents(transfer);
  const cardCents = toCents(card);
  const creditCents = toCents(credit);
  const collected = cashCents + transferCents + cardCents;
  const covered = collected + creditCents;
  const change = Math.max(0, receivedCents - cashCents);
  const balance = Math.max(0, totalCents - collected);
  const valid = mode === 'sale'
    ? covered === totalCents && receivedCents >= cashCents
    : collected > 0 && collected <= totalCents && Boolean(customer);
  const singleMethod = cashCents > 0 && !cardCents && !transferCents && !creditCents
    ? 'cash'
    : cardCents > 0 && !cashCents && !transferCents && !creditCents
      ? 'card'
      : transferCents > 0 && !cashCents && !cardCents && !creditCents
        ? 'transfer'
        : 'mixed';
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const tenderSuggestions = Array.from(new Set([
    quickAmountCents,
    ...[50, 100, 200, 500, 1000].map((denomination) => Math.ceil(quickAmountCents / (denomination * 100)) * denomination * 100),
  ])).filter((value) => value >= quickAmountCents).slice(0, 4);

  const chooseMethod = (method: 'cash' | 'card' | 'transfer') => {
    setCash(method === 'cash' ? amount : '');
    setReceived(method === 'cash' ? amount : '');
    setCard(method === 'card' ? amount : '');
    setTransfer(method === 'transfer' ? amount : '');
    setCredit('');
    setAdvancedOpen(false);
  };

  const submit = () => {
    const payments: PaymentInput['payments'] = [];
    if (cashCents) payments.push({ method: 'cash', amountCents: cashCents });
    if (transferCents) payments.push({ method: 'transfer', amountCents: transferCents });
    if (cardCents) payments.push({ method: 'card', amountCents: cardCents });
    if (creditCents) payments.push({ method: 'credit', amountCents: creditCents });
    onComplete({
      payments,
      changeCents: change,
      dueDate: mode === 'layaway' || (mode === 'sale' && creditCents > 0) ? dueDate : undefined,
      promisedDate: mode === 'special-order' ? promisedDate : undefined,
      notes,
    });
  };

  const coverageLabel = mode !== 'sale'
    ? `Quedarán ${formatMoney(balance)} pendientes`
    : covered < totalCents
      ? `Faltan ${formatMoney(totalCents - covered)}`
      : covered > totalCents
        ? `Sobran ${formatMoney(covered - totalCents)}`
        : receivedCents < cashCents
          ? `Faltan ${formatMoney(cashCents - receivedCents)} en efectivo`
          : 'Pago completo';

  return (
    <Modal open={open} onClose={onClose} size="wide" title={mode === 'sale' ? 'Cobrar venta' : mode === 'layaway' ? 'Crear apartado' : 'Registrar encargo'}>
      <div className="payment-panel payment-panel--fast">
        <section className="payment-hero">
          <div>
            <span>{mode === 'sale' ? 'Total a cobrar' : 'Total de la operación'}</span>
            <strong>{formatMoney(totalCents)}</strong>
          </div>
          <small>{itemCount} {itemCount === 1 ? 'artículo' : 'artículos'}{customer ? ` · ${customer.name}` : ' · Mostrador'}</small>
        </section>

        <section className="payment-method-picker" aria-label="Forma de pago">
          <span>¿Cómo te pagaron?</span>
          <div>
            <PaymentMethodButton active={singleMethod === 'cash'} icon={<Banknote size={23}/>} title="Efectivo" detail="Calcula el cambio" onClick={() => chooseMethod('cash')}/>
            <PaymentMethodButton active={singleMethod === 'card'} icon={<CreditCard size={23}/>} title="Tarjeta" detail="Terminal externa" onClick={() => chooseMethod('card')}/>
            <PaymentMethodButton active={singleMethod === 'transfer'} icon={<ArrowRightLeft size={23}/>} title="Transferencia" detail="Registro manual" onClick={() => chooseMethod('transfer')}/>
          </div>
        </section>

        {singleMethod === 'cash' && !advancedOpen && (
          <section className="cash-fast-entry">
            <label className="field">
              <span>¿Con cuánto te pagaron?</span>
              <span className="money-input money-input--checkout"><b>$</b><input aria-label="Efectivo recibido" value={received} onChange={(event) => setReceived(event.target.value)} inputMode="decimal" autoComplete="off" /></span>
            </label>
            <div className="cash-suggestions">
              {tenderSuggestions.map((value, index) => <button type="button" key={value} className={receivedCents === value ? 'is-active' : ''} onClick={() => setReceived((value / 100).toFixed(2))}>{index === 0 ? 'Exacto' : formatMoney(value)}</button>)}
            </div>
            <div className={`cash-change ${receivedCents >= cashCents ? 'is-ready' : 'is-pending'}`}><span>Cambio a entregar</span><strong>{formatMoney(change)}</strong></div>
          </section>
        )}

        {singleMethod !== 'cash' && singleMethod !== 'mixed' && !advancedOpen && (
          <div className="payment-method-note"><CheckCircle2 size={20}/><span><strong>{singleMethod === 'card' ? 'Cobro con terminal externa' : 'Transferencia confirmada'}</strong><small>Se registrarán manualmente {formatMoney(quickAmountCents)} en esta venta.</small></span></div>
        )}

        <button type="button" className={`payment-advanced-toggle ${advancedOpen ? 'is-open' : ''}`} onClick={() => setAdvancedOpen((current) => !current)}>
          <WalletCards size={18}/><span><strong>Pago mixto o importe diferente</strong><small>Divide entre efectivo, tarjeta, transferencia o crédito</small></span><ChevronDown size={18}/>
        </button>

        {advancedOpen && (
          <section className="payment-advanced-panel">
            <div className="payment-grid">
              <MoneyField label="Efectivo aplicado" value={cash} onChange={setCash}/>
              <MoneyField label="Efectivo recibido" value={received} onChange={setReceived}/>
              <MoneyField label="Tarjeta" value={card} onChange={setCard}/>
              <MoneyField label="Transferencia" value={transfer} onChange={setTransfer}/>
              {mode === 'sale' && <MoneyField label="Crédito" value={credit} onChange={setCredit} disabled={!customer} hint={!customer ? 'Selecciona un cliente para habilitarlo' : undefined}/>} 
              {mode === 'sale' && creditCents > 0 && <label className="field"><span>Vencimiento del crédito</span><input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} /></label>}
              {mode === 'layaway' && <label className="field"><span>Fecha límite</span><input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} /></label>}
              {mode === 'special-order' && <label className="field"><span>Fecha prometida</span><input type="date" value={promisedDate} onChange={(event) => setPromisedDate(event.target.value)} /></label>}
            </div>
            {cashCents > 0 && <div className={`payment-split-change ${receivedCents >= cashCents ? 'is-ready' : 'is-pending'}`}><span>Cambio a entregar</span><strong>{formatMoney(change)}</strong></div>}
            <label className="field payment-notes"><span>Notas opcionales</span><textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Referencia, condiciones o indicaciones" /></label>
          </section>
        )}

        {mode !== 'sale' && <div className="commitment-summary"><span>Cliente</span><strong>{customer?.name ?? 'Selecciona un cliente'}</strong><span>Anticipo aplicado</span><strong>{formatMoney(collected)}</strong><span>Saldo pendiente</span><strong>{formatMoney(balance)}</strong></div>}

        <footer className="payment-submitbar">
          <div className={valid ? 'is-valid' : ''}><span>{coverageLabel}</span><strong>{formatMoney(mode === 'sale' ? covered : collected)} de {formatMoney(totalCents)}</strong></div>
          <Button size="lg" onClick={submit} disabled={!valid}>Confirmar {mode === 'sale' ? 'cobro' : mode === 'layaway' ? 'apartado' : 'encargo'} <ArrowRight size={18}/></Button>
        </footer>
      </div>
    </Modal>
  );
}

function PaymentMethodButton({ active, icon, title, detail, onClick }: { active: boolean; icon: ReactNode; title: string; detail: string; onClick: () => void }) {
  return <button type="button" className={active ? 'is-active' : ''} onClick={onClick}>{icon}<strong>{title}</strong><small>{detail}</small>{active && <CheckCircle2 className="payment-method-check" size={17}/>}</button>;
}

function MoneyField({ label, value, onChange, disabled, hint }: { label: string; value: string; onChange: (value: string) => void; disabled?: boolean; hint?: string }) {
  return <label className="field"><span>{label}</span><span className="money-input"><b>$</b><input aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} inputMode="decimal" autoComplete="off" disabled={disabled} /></span>{hint && <small className="field-hint">{hint}</small>}</label>;
}
